import dynamic from 'next/dynamic';
import { Fragment, useRef, useState, type ReactNode } from 'react';
import { Bell, ChevronRight, Flag, Folder, Link2, Pencil, Settings2, UserPlus, UsersRound } from 'lucide-react';
import { GROUP_AVATAR, groupInitials, groupTitle, leadingPhoto, memberCountLine, participantLabel, personName, showsMemberCount, viewerRole, type GroupMember, type GroupSummary } from '../../../lib/group-chat';
import { memberActions, outcomeErrorKey, type InviteWire } from '../../../lib/group-chat-actions';
import { applyGroupEnded, dropGroupConversation, groupChatAction, handleLostAccess, showGroupNotice } from '../../../lib/group-chat-api';
import {
  adminErrorKey,
  blockOffered,
  capabilitiesFor,
  infoRows,
  parseBlockedPerson,
  requestsBadge,
  updateInfoBody,
  withBlocked,
  type BlockedPerson,
} from '../../../lib/group-admin';
import { uploadAttachment } from '../../../lib/attachments';
import { avatarSlotFor } from '../../../lib/avatar-color';
import { patchChatsConversation } from '../../../lib/chats';
import { queryClient, queryKeys } from '../../../lib/query-client';
import { groupMembersKey, useGroupMembers, useGroupNotifications } from '../../../lib/use-group-summary';
import { notificationsRowValue } from '../../../lib/group-notifications';
import { GroupNotificationsDialog } from './GroupNotifications';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { Modal } from '../../Modal';
import { Avatar, Button, IconButton, OverflowMenu, Typography } from '../ui';
import { GroupAvatarStack, GroupLeadingImage, GroupParticipantAvatar } from './GroupAvatars';
import { AddPeopleDialog, GroupConfirmDialog, RenameGroupDialog } from './GroupChatDialogs';
import { AddMaestroDialog, RemoveMaestroDialog } from './GroupMaestro';
import {
  blockedKey,
  GroupDescriptionDialog,
  GroupSettingsDialog,
  InviteLinkDialog,
  JoinRequestsDialog,
  ReportDialog,
  unblockPerson,
  useBlockedPeople,
  useJoinRequests,
} from './GroupAdminDialogs';

import { canAddMyMaestro, canRemoveMaestro, canStopMaestro, GROUP_MAESTRO_STOPPED_EVENT, ownMaestroGroupName, maestroRows, stopOutcome, stopRowLine, stopRowLineFor, type StopRowLine } from '../../../lib/group-maestro';
import { useGroupChatEnabled } from '../../../lib/group-chat-flag';
import { useAgentName } from '../../../lib/agent-name-context';
import type { GroupParticipant } from '../../../lib/group-chat';
import { headerPresenceLine, memberPresenceLine, typingLine } from '../../../lib/group-receipts';
import { useGroupPresence, useGroupTyping } from '../../../lib/group-live-state';

// Loaded on use: the group-files UI brings its viewer (markdown) and data layer, which this host never needs until then.
const GroupFilesDialog = dynamic(() => import('./GroupFiles').then((m) => m.GroupFilesDialog), { ssr: false });

// Loaded on open: the settings dialog brings the whole file explorer (its picker), which the chat header never needs
// until the owner opens it.
const MaestroSettingsDialog = dynamic(() => import('./MaestroSettingsDialog').then((m) => m.MaestroSettingsDialog), { ssr: false });

/**
 * Inside a group chat: the group's photo when it has one, else the stacked avatars (GROUP_AVATAR.header — 24 Ø,
 * step 12), the group's name and its member count. A tap opens the group info (Phase 10a). iOS `GroupChatHeaderLine`
 * / Android `GroupChatHeaderBlock` draw the same.
 */
export function GroupChatHeader({ summary, fallbackTitle, conversationId }: { summary: GroupSummary; fallbackTitle: string; conversationId?: string }) {
  const { t, i18n } = useTranslation();
  // Design §21: the subtitle says who is typing (a Maestro: thinking), else who is online / last seen, else the count.
  const live = !summary.ended;
  const typing = useGroupTyping(live ? conversationId ?? summary.conversationId : null);
  const { presence, now } = useGroupPresence(live);
  const typingText = live ? typingLine(typing.entries, summary.participants, summary.viewerId, t, typing.now) : null;
  const presenceText = live && !typingText ? headerPresenceLine(summary, presence, now, t, i18n?.language) : null;
  const { logPageClickBi } = useGenericBi();
  const [open, setOpen] = useState(false);
  // Never fetched from here: a `members` read the info sheet already made is the fresher photo (see `leadingPhoto`).
  const members = useGroupMembers(conversationId ?? summary.conversationId, false);
  return (
    <>
      <Button
        variant="text"
        size="small"
        data-testid="group-chat-header"
        aria-label={t('chat.group.info.open')}
        onClick={() => {
          logPageClickBi({ page_name: 'chat', button_name: 'group_info_open' });
          setOpen(true);
        }}
        style={{ minWidth: 0, maxWidth: '100%', textAlign: 'start' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: GROUP_AVATAR.header.gap, minWidth: 0 }}>
        <GroupLeadingImage summary={summary} preset="header" photoUrl={leadingPhoto(summary, members.data?.details)} />
        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontSize: 14, lineHeight: '18px', fontWeight: 'var(--sym-weight-semibold)', color: 'var(--ds-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {groupTitle(summary, fallbackTitle)}
          </span>
          {/* Typing, else online / last seen, else the count. An ended group says no count: the end left only its owner. */}
          {(typingText || presenceText || showsMemberCount(summary)) && (
            <span data-testid={typingText ? 'group-header-typing' : presenceText ? 'group-header-presence' : undefined}
              style={{ fontSize: 12, lineHeight: '16px', color: typingText ? 'var(--accent-solid)' : 'var(--ds-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {typingText ?? presenceText ?? memberCountLine(summary.memberCount, t)}
            </span>
          )}
        </span>
        </span>
      </Button>
      {open && <GroupParticipantsSheet summary={summary} conversationId={conversationId ?? summary.conversationId} onClose={() => setOpen(false)} />}
    </>
  );
}

const sectionTitle = { fontSize: 12, lineHeight: '16px', fontWeight: 'var(--sym-weight-semibold)', color: 'var(--ds-text-secondary)', margin: '16px 0 8px' } as const;

type Pending =
  | { kind: 'remove'; member: { id: string; name: string } }
  | { kind: 'leave' }
  | { kind: 'end' }
  | { kind: 'rename' }
  | { kind: 'add' }
  | { kind: 'addMaestro' }
  | { kind: 'removeMaestro'; maestro: GroupParticipant; label: string }
  | { kind: 'maestroSettings' }
  | { kind: 'files' }
  // Phase 10a: the group info's own dialogs.
  | { kind: 'settings' }
  | { kind: 'link' }
  | { kind: 'requests' }
  | { kind: 'report' }
  | { kind: 'description' }
  | { kind: 'photo' }
  | { kind: 'block'; member: { id: string; name: string } }
  | { kind: 'notifications' }
  | null;

/**
 * GROUP INFO (Phase 10a, spec item 1) — the participant sheet grown into WhatsApp's group info, ONE list of people:
 * the photo, name and member count, the description, Group settings / Invite via link / Join requests (each by the
 * caller's capabilities), the files, the participants with Block / Unblock, then Report group and Leave / End.
 *
 * The participant list (contract item 7): people (owner/admin marked, the viewer as "(you)"), then each Maestro by
 * its label. With a conversation id it loads `members` on open for roles and (admins) pending invites, and offers:
 * Make admin / Remove as admin (owner), Remove from group (owner; admins on plain members), Cancel invite (admins),
 * Rename group (admins), Add people (owner, admins), Leave group (non-owners) and End group (owner). Every
 * destructive step confirms; every refusal shows its mapped message. Without an id it stays the read-only 3a list.
 */
export function GroupParticipantsSheet({ summary, conversationId, onClose, initialEnd }: { summary: GroupSummary; conversationId?: string; onClose: () => void; initialEnd?: boolean }) {
  const { t, i18n: i18nSheet } = useTranslation();
  const livePresence = useGroupPresence(!summary.ended);
  const { logModalActionBi } = useGenericBi();
  const members = useGroupMembers(conversationId, !!conversationId);
  const [pending, setPending] = useState<Pending>(initialEnd ? { kind: 'end' } : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Phase 8: what the last Stop left, drawn under that Maestro's row (as iOS and Android). */
  const [stopLine, setStopLine] = useState<StopRowLine | null>(null);
  const bi = (action: string) => logModalActionBi({ page_name: 'chat', modal_name: 'group_participants', action } as never);

  /** Phase 8: stop a Maestro's work here now (its owner or an admin); what it already did stays done. */
  const stopMaestro = async (maestro: GroupParticipant) => {
    if (!conversationId || !maestro.ownerId) return;
    setBusy(true);
    setError(null);
    setStopLine(null);
    const outcome = await groupChatAction('stop-maestro-work', { conversationId, ownerMetaSiteId: maestro.ownerId });
    setBusy(false);
    if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) {
      onClose();
      return;
    }
    const result = stopOutcome(outcome);
    setStopLine(stopRowLine(maestro.ownerId, result));
    if (result.kind === 'stopped') {
      window.dispatchEvent(new CustomEvent(GROUP_MAESTRO_STOPPED_EVENT, { detail: { conversationId, ownerId: maestro.ownerId } }));
    }
  };

  const read = members.data;
  const role = read?.yourRole ?? viewerRole(summary);
  const isAdmin = role === 'OWNER' || role === 'ADMIN';
  // Phase 10a: what the caller may do here, the group's own identity, the pending requests and the blocked list.
  const caps = capabilitiesFor(read, role);
  const rowsOffered = infoRows(caps);
  const details = read?.details ?? null;
  const groupOn = useGroupChatEnabled();
  // The caller's own notification mode here (owner and members); null from an older service → no row.
  const notifications = useGroupNotifications(conversationId);
  const requests = useJoinRequests(conversationId, !!conversationId && rowsOffered.requests);
  const requestCount = requestsBadge(requests.data?.length ?? 0);
  const blocked = useBlockedPeople(!!conversationId && groupOn);
  const blockedIds = new Set((blocked.data ?? []).map((b) => b.metaSiteId));
  const { logPageClickBi } = useGenericBi();
  const click = (button: string) => logPageClickBi({ page_name: 'chat', button_name: button });

  /** `update-info` with only what changed; a failure keeps the dialog open with its message. */
  const saveInfo = async (change: { name?: string; description?: string; photoUrl?: string }, failureKey: string): Promise<boolean> => {
    if (!conversationId) return false;
    const body = updateInfoBody(conversationId, { name: details?.name ?? summary.name ?? '', description: details?.description ?? '', photoUrl: details?.photoUrl ?? null }, change);
    if (!body) return true;
    setBusy(true);
    setError(null);
    const outcome = await groupChatAction('update-info', body);
    setBusy(false);
    if (outcome.kind === 'ok') {
      refresh();
      return true;
    }
    if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) {
      onClose();
      return false;
    }
    setError(t(adminErrorKey(outcome, failureKey)));
    return false;
  };

  /** Block one member (after the confirm); its toast; the blocked list gains them. */
  const block = async (member: { id: string; name: string }) => {
    click('person_block');
    setBusy(true);
    setError(null);
    const outcome = await groupChatAction('block', { metaSiteId: member.id });
    setBusy(false);
    if (outcome.kind !== 'ok') {
      setError(t(outcome.kind === 'refused' ? adminErrorKey(outcome) : 'chat.group.block.failed'));
      return;
    }
    const person: BlockedPerson = parseBlockedPerson(outcome.data.blocked) ?? { metaSiteId: member.id, name: member.name, avatarUrl: null, blockedDate: null };
    queryClient.setQueryData<BlockedPerson[]>(blockedKey, (list) => withBlocked(list ?? [], person));
    setPending(null);
    showGroupNotice('chat.group.block.blocked', { name: member.name });
  };
  // Roles come from `members` when it answered; the summary's participants until then.
  const roleById = new Map<string, GroupMember>((read?.members ?? []).map((m) => [m.metaSiteId, m]));
  const people = summary.participants.filter((p) => p.kind === 'HUMAN');
  for (const m of read?.members ?? []) {
    if (!people.some((p) => p.id === m.metaSiteId)) people.push({ id: m.metaSiteId, kind: 'HUMAN', displayName: personName(m.name, m.role, t), ...(m.name.trim() ? {} : { defaultNamed: true }), role: m.role, ...(m.avatarUrl ? { avatarUrl: m.avatarUrl } : {}) });
  }
  const assistants = maestroRows(summary);
  // Phase 5: every member's own Maestro is listed; the viewer can add theirs, open its settings, and remove it.
  const groupEnabled = useGroupChatEnabled();
  const ownAgent = useAgentName();
  const offerAddMaestro = !!conversationId && canAddMyMaestro(summary, groupEnabled);
  const invites: InviteWire[] = isAdmin ? read?.pendingInvites ?? [] : [];

  const refresh = () => {
    if (!conversationId) return;
    void queryClient.invalidateQueries({ queryKey: groupMembersKey(conversationId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.conversation(conversationId) });
    patchChatsConversation(conversationId, {});
  };

  /** Run one action; a lost group closes everything (the chat leaves with a notice). */
  const run = async (action: Parameters<typeof groupChatAction>[0], body: Record<string, unknown>, onOk: (data: Record<string, unknown>) => void) => {
    if (!conversationId) return;
    setBusy(true);
    setError(null);
    setStopLine(null);
    const outcome = await groupChatAction(action, { conversationId, ...body });
    setBusy(false);
    if (outcome.kind === 'ok') {
      onOk(outcome.data);
      return;
    }
    if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) {
      onClose();
      return;
    }
    // The owner cannot leave a group with others in it: offer End group instead.
    if (outcome.kind === 'refused' && outcome.refusal === 'OWNER_MUST_END') {
      setPending({ kind: 'end' });
      setError(t('chat.group.error.ownerMustEnd'));
      return;
    }
    setError(t(outcomeErrorKey(outcome)));
  };

  const presenceLineFor = (p: GroupParticipant): string | null =>
    p.kind === 'HUMAN' && p.id !== summary.viewerId && !summary.ended ? memberPresenceLine(livePresence.presence[p.id], livePresence.now, t, i18nSheet?.language) : null;
  const row = (p: GroupSummary['participants'][number]) => {
    const you = p.kind === 'HUMAN' && p.id === summary.viewerId;
    const name = you ? t('chat.group.youSuffix', { name: p.displayName }) : participantLabel(p, t);
    const memberRole = roleById.get(p.id)?.role ?? p.role;
    const roleLabel = p.kind === 'HUMAN' ? (memberRole === 'OWNER' ? t('chat.group.roleOwner') : memberRole === 'ADMIN' ? t('chat.group.roleAdmin') : null) : null;
    const can = p.kind === 'HUMAN' && conversationId ? memberActions(role, { role: memberRole, you }) : { makeAdmin: false, removeAdmin: false, remove: false };
    const ownMaestro = p.kind === 'MAESTRO' && !!summary.viewerId && p.ownerId === summary.viewerId;
    const removableMaestro = p.kind === 'MAESTRO' && !!conversationId && !!p.ownerId && canRemoveMaestro(summary, p);
    const stoppableMaestro = p.kind === 'MAESTRO' && !!conversationId && !!p.ownerId && canStopMaestro(summary, p);
    const actions = [
      ...(stoppableMaestro ? [{ key: 'stop-maestro', label: t('chat.group.maestro.stop'), onSelect: () => { bi('stop_maestro'); void stopMaestro(p); } }] : []),
      ...(removableMaestro ? [{ key: 'remove-maestro', label: t('chat.group.removeMember'), destructive: true, onSelect: () => { bi('remove_maestro_open'); setError(null); setPending({ kind: 'removeMaestro', maestro: p, label: name }); } }] : []),
      ...(can.makeAdmin ? [{ key: 'make-admin', label: t('chat.group.makeAdmin'), onSelect: () => { bi('make_admin'); void run('set-role', { memberMetaSiteId: p.id, role: 'ADMIN' }, refresh); } }] : []),
      ...(can.removeAdmin ? [{ key: 'remove-admin', label: t('chat.group.removeAdmin'), onSelect: () => { bi('remove_admin'); void run('set-role', { memberMetaSiteId: p.id, role: 'MEMBER' }, refresh); } }] : []),
      ...(can.remove ? [{ key: 'remove', label: t('chat.group.removeMember'), destructive: true, onSelect: () => { bi('remove_member_open'); setError(null); setPending({ kind: 'remove', member: { id: p.id, name: p.displayName } }); } }] : []),
      // Phase 10a: Block / Unblock every other human (never me, never a Maestro). Block confirms; unblock is immediate.
      ...(conversationId && groupOn && blockOffered(p, summary.viewerId)
        ? blockedIds.has(p.id)
          ? [{ key: 'unblock', label: t('chat.group.block.unblockName', { name: p.displayName }), onSelect: () => { click('person_unblock'); void unblockPerson({ metaSiteId: p.id, name: p.displayName }); } }]
          : [{ key: 'block', label: t('chat.group.block.blockName', { name: p.displayName }), destructive: true, onSelect: () => { setError(null); setPending({ kind: 'block', member: { id: p.id, name: p.displayName } }); } }]
        : []),
    ];
    const isBlocked = p.kind === 'HUMAN' && blockedIds.has(p.id);
    const line = p.kind === 'MAESTRO' ? stopRowLineFor(stopLine, p.ownerId) : null;
    return (
      <Fragment key={p.id}>
      <li data-testid={`group-participant-${p.id}`} style={{ display: 'flex', alignItems: 'center', gap: GROUP_AVATAR.sheet.gap, padding: '6px 0' }}>
        {ownMaestro && conversationId ? (
          // The viewer's own Maestro: the row opens "{agentName} in this group".
          <Button variant="text" onClick={() => { bi('maestro_settings_open'); setPending({ kind: 'maestroSettings' }); }} data-testid="group-own-maestro"
            style={{ flex: 1, minWidth: 0, justifyContent: 'flex-start', textAlign: 'start', padding: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: GROUP_AVATAR.sheet.gap, minWidth: 0 }}>
              <GroupParticipantAvatar participant={p} size={GROUP_AVATAR.sheet.size} />
              <span style={{ fontSize: 14, lineHeight: '18px', color: 'var(--ds-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
            </span>
          </Button>
        ) : (
          <>
            <GroupParticipantAvatar participant={p} size={GROUP_AVATAR.sheet.size} />
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 14, lineHeight: '18px', color: 'var(--ds-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
              {/* Design §21: each other person's "online" / "last seen …" (nothing when they or the viewer hide it). */}
              {presenceLineFor(p) && (
                <span data-testid={`group-participant-presence-${p.id}`} style={{ fontSize: 12, lineHeight: '16px', color: 'var(--ds-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{presenceLineFor(p)}</span>
              )}
            </span>
          </>
        )}
        {isBlocked && <span data-testid="group-blocked-badge" style={{ fontSize: 12, lineHeight: '16px', color: 'var(--ds-text-destructive)' }}>{t('chat.group.block.badge')}</span>}
        {roleLabel && <span style={{ fontSize: 12, lineHeight: '16px', color: 'var(--ds-text-secondary)' }}>{roleLabel}</span>}
        {actions.length > 0 && <OverflowMenu ariaLabel={p.displayName} actions={actions} />}
      </li>
      {line && (
        <li role={line.isError ? 'alert' : 'status'} data-testid={line.isError ? 'group-maestro-stop-error' : 'group-maestro-stopped'}
          style={{ paddingInlineStart: GROUP_AVATAR.sheet.size + GROUP_AVATAR.sheet.gap, paddingBottom: 6, fontSize: 14, lineHeight: '20px',
            color: line.isError ? 'var(--ds-text-destructive)' : 'var(--ds-text-secondary)' }}>
          {t(line.messageKey, { maestroAgentName: p.displayName })}
        </li>
      )}
      </Fragment>
    );
  };

  const footer = conversationId ? (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, width: '100%', justifyContent: 'flex-end' }}>
      {(
        <Button variant="text" color="destructive" startIcon={<Flag size={16} aria-hidden />} disabled={busy} onClick={() => { setError(null); setPending({ kind: 'report' }); }} data-testid="group-report-open">
          {t('chat.group.report.group')}
        </Button>
      )}
      {role === 'OWNER' ? (
        <Button variant="outlined" color="destructive" disabled={busy} onClick={() => { bi('end_open'); setError(null); setPending({ kind: 'end' }); }} data-testid="group-end">{t('chat.group.end')}</Button>
      ) : (
        <Button variant="outlined" color="destructive" disabled={busy} onClick={() => { bi('leave_open'); setError(null); setPending({ kind: 'leave' }); }} data-testid="group-leave">{t('chat.group.leave')}</Button>
      )}
      {caps.canAddPeople && <Button startIcon={<UserPlus size={16} aria-hidden />} disabled={busy} onClick={() => { bi('add_people_open'); setPending({ kind: 'add' }); }} data-testid="group-add-people-open">{t('chat.group.addPeople')}</Button>}
    </div>
  ) : undefined;

  if (pending?.kind === 'add' && conversationId) {
    return <AddPeopleDialog conversationId={conversationId} summary={summary} onClose={() => { setPending(null); refresh(); }} />;
  }
  if (pending?.kind === 'addMaestro' && conversationId) {
    // The name the group will know it by: the renamed agent's own, else "{you}'s Maestro" (lib/group-maestro.ts `ownMaestroGroupName`).
    return <AddMaestroDialog conversationId={conversationId} agentName={ownMaestroGroupName(ownAgent, summary, t)} onClose={() => setPending(null)} />;
  }
  if (pending?.kind === 'removeMaestro' && conversationId) {
    return <RemoveMaestroDialog conversationId={conversationId} maestro={pending.maestro} label={pending.label} onClose={() => setPending(null)} />;
  }
  if (pending?.kind === 'maestroSettings' && conversationId) {
    return <MaestroSettingsDialog conversationId={conversationId} agentName={ownAgent.short} addName={ownMaestroGroupName(ownAgent, summary, t)} onClose={() => setPending(null)} />;
  }
  if (pending?.kind === 'files' && conversationId) {
    return <GroupFilesDialog conversationId={conversationId} groupName={groupTitle(summary, t('chat.group.files.aGroupChat'))} onClose={() => setPending(null)} />;
  }
  if (pending?.kind === 'rename') {
    return (
      <RenameGroupDialog initial={details?.name ?? summary.name ?? ''} busy={busy} error={error} onClose={() => setPending(null)}
        onSave={(name) => { bi('rename_save'); void saveInfo({ name }, 'chat.group.info.saveFailed').then((ok) => ok && setPending(null)); }} />
    );
  }
  if (pending?.kind === 'description') {
    return (
      <GroupDescriptionDialog initial={details?.description ?? ''} busy={busy} error={error} onClose={() => setPending(null)}
        onSave={(description) => { bi('description_save'); void saveInfo({ description }, 'chat.group.info.saveFailed').then((ok) => ok && setPending(null)); }} />
    );
  }
  if (pending?.kind === 'photo' && conversationId) {
    return (
      <GroupPhotoDialog hasPhoto={!!details?.photoUrl} busy={busy} error={error} onClose={() => setPending(null)}
        onUploading={(on) => setBusy(on)}
        onFailed={() => setError(t('chat.group.info.photoFailed'))}
        onSave={(photoUrl) => { void saveInfo({ photoUrl }, 'chat.group.info.photoFailed').then((ok) => ok && setPending(null)); }} />
    );
  }
  if (pending?.kind === 'settings' && conversationId && details) {
    return <GroupSettingsDialog conversationId={conversationId} settings={details.settings} onClose={() => setPending(null)} />;
  }
  if (pending?.kind === 'notifications' && conversationId && notifications) {
    return <GroupNotificationsDialog conversationId={conversationId} notifications={notifications} onClose={() => setPending(null)} />;
  }
  if (pending?.kind === 'link' && conversationId) {
    return <InviteLinkDialog conversationId={conversationId} canReset={caps.canModerate} onClose={() => setPending(null)} />;
  }
  if (pending?.kind === 'requests' && conversationId) {
    return <JoinRequestsDialog conversationId={conversationId} onClose={() => setPending(null)} />;
  }
  if (pending?.kind === 'report' && conversationId) {
    return <ReportDialog conversationId={conversationId} target={{ kind: 'group' }} role={role} onClose={() => setPending(null)} />;
  }
  if (pending?.kind === 'block') {
    return (
      <GroupConfirmDialog title={t('chat.group.block.confirmTitle', { name: pending.member.name })} body={t('chat.group.block.confirmBody')}
        confirmLabel={t('chat.group.block.block')} busy={busy} error={error} onClose={() => setPending(null)}
        onConfirm={() => void block(pending.member)} />
    );
  }
  if (pending?.kind === 'remove') {
    return (
      <GroupConfirmDialog title={t('chat.group.removeMember')} body={t('chat.group.removeConfirm', { name: pending.member.name })}
        confirmLabel={t('chat.group.removeMember')} busy={busy} error={error} onClose={() => setPending(null)}
        onConfirm={() => { bi('remove_member'); void run('remove-member', { memberMetaSiteId: pending.member.id }, () => { setPending(null); refresh(); }); }} />
    );
  }
  if (pending?.kind === 'leave') {
    return (
      <GroupConfirmDialog title={t('chat.group.leave')} body={t('chat.group.leaveConfirm')} confirmLabel={t('chat.group.leave')}
        busy={busy} error={error} onClose={() => setPending(null)}
        onConfirm={() => { bi('leave'); void run('leave', {}, () => { onClose(); dropGroupConversation(conversationId!, 'chat.group.removedNotice'); }); }} />
    );
  }
  if (pending?.kind === 'end') {
    return (
      <GroupConfirmDialog title={t('chat.group.endConfirmTitle')} body={t('chat.group.endConfirm')} confirmLabel={t('chat.group.end')}
        busy={busy} error={error} onClose={() => (initialEnd ? onClose() : setPending(null))}
        onConfirm={() => { bi('end'); void run('end', {}, () => { onClose(); applyGroupEnded(conversationId!, t('chat.group.you')); }); }} />
    );
  }

  return (
    <Modal open onClose={onClose} title={t('chat.group.info.title')} size="minimum" maxHeightPx={650} scrollBody headerSeparator footer={footer}>
      {conversationId && (
        <GroupIdentity
          summary={summary}
          title={groupTitle({ ...summary, name: details?.name ?? summary.name }, t('chat.group.indicator'))}
          photoUrl={details?.photoUrl ?? null}
          description={details?.description ?? ''}
          canEdit={caps.canEditInfo}
          onPhoto={() => { click('group_info_photo'); setError(null); setPending({ kind: 'photo' }); }}
          onName={() => { bi('rename_open'); setError(null); setPending({ kind: 'rename' }); }}
          onDescription={() => { click('group_info_description'); setError(null); setPending({ kind: 'description' }); }}
        />
      )}
      {conversationId && (
        <ul data-testid="group-info-rows" style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {rowsOffered.settings && details && (
            <InfoRow icon={<Settings2 size={16} aria-hidden />} label={t('chat.group.settings.title')} testId="group-settings-open" onClick={() => { click('group_settings_open'); setPending({ kind: 'settings' }); }} />
          )}
          {notifications && (
            <InfoRow icon={<Bell size={16} aria-hidden />} label={t('chat.group.notify.title')} value={notificationsRowValue(notifications, t, ownAgent.short)}
              testId="group-notifications-open" onClick={() => { click('group_notifications_open'); setPending({ kind: 'notifications' }); }} />
          )}
          {rowsOffered.link && (
            <InfoRow icon={<Link2 size={16} aria-hidden />} label={t('chat.group.link.title')} testId="group-link-open" onClick={() => { click('group_link_open'); setPending({ kind: 'link' }); }} />
          )}
          {rowsOffered.requests && (
            <InfoRow icon={<UsersRound size={16} aria-hidden />} label={t('chat.group.requests.title')} badge={requestCount} testId="group-requests-open" onClick={() => { click('group_requests_open'); setPending({ kind: 'requests' }); }} />
          )}
        </ul>
      )}
      {/* Phase 7: the files shared into this group (the FLAT family — see GroupFilesDialog's written-down exception). */}
      {conversationId && groupEnabled && (
        <Button variant="outlined" startIcon={<Folder size={16} aria-hidden />} fullWidth data-testid="group-files-open"
          onClick={() => { bi('files_open'); setPending({ kind: 'files' }); }} style={{ marginTop: 8 }}>
          {t('chat.group.files.entry')}
        </Button>
      )}
      <h3 style={sectionTitle}>{conversationId ? t('chat.group.participantsTitle') : t('chat.group.peopleSection')}</h3>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{people.map(row)}</ul>
      {(assistants.length > 0 || offerAddMaestro) && (
        <>
          <h3 style={sectionTitle}>{t('chat.group.assistantsSection')}</h3>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {assistants.map(row)}
            {offerAddMaestro && (
              <li style={{ display: 'flex', padding: '6px 0' }}>
                <Button variant="text" onClick={() => { bi('add_maestro_open'); setPending({ kind: 'addMaestro' }); }} data-testid="group-add-my-maestro"
                  style={{ flex: 1, minWidth: 0, justifyContent: 'flex-start', textAlign: 'start', padding: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: GROUP_AVATAR.sheet.gap, minWidth: 0 }}>
                    <GroupParticipantAvatar participant={{ id: 'add-my-maestro', kind: 'MAESTRO', displayName: ownAgent.short, role: 'MEMBER' }} size={GROUP_AVATAR.sheet.size} />
                    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <span style={{ fontSize: 14, lineHeight: '18px', color: 'var(--ds-text-primary)' }}>{t('chat.group.maestro.add')}</span>
                      <span style={{ fontSize: 12, lineHeight: '16px', color: 'var(--ds-text-secondary)' }}>{t('chat.group.maestro.addHint')}</span>
                    </span>
                  </span>
                </Button>
              </li>
            )}
          </ul>
        </>
      )}
      {invites.length > 0 && (
        <>
          <h3 style={sectionTitle}>{t('chat.group.pendingInvites')}</h3>
          <ul data-testid="group-pending-invites" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {invites.map((inv) => (
              <li key={inv.inviteId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 14, lineHeight: '18px', color: 'var(--ds-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.email}</span>
                <Button size="small" variant="text" disabled={busy}
                  onClick={() => { bi('revoke_invite'); void run('revoke-invite', { inviteId: inv.inviteId }, refresh); }}>
                  {t('chat.group.revokeInvite')}
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}
      {error && <Typography variant="p2" component="p" role="alert" style={{ color: 'var(--ds-text-destructive)', marginTop: 12 }}>{error}</Typography>}
    </Modal>
  );
}

/** The group's photo (else its avatar stack), name, member count and description — tappable when the caller may edit. */
function GroupIdentity({ summary, title, photoUrl, description, canEdit, onPhoto, onName, onDescription }: {
  summary: GroupSummary;
  title: string;
  photoUrl: string | null;
  description: string;
  canEdit: boolean;
  onPhoto: () => void;
  onName: () => void;
  onDescription: () => void;
}) {
  const { t } = useTranslation();
  const [photoFailed, setPhotoFailed] = useState(false);
  const photo = photoUrl && !photoFailed
    ? <Avatar size={72} initials={groupInitials(title)} photoUrl={photoUrl} onPhotoError={() => setPhotoFailed(true)} color={avatarSlotFor(summary.conversationId ?? title)} />
    : <GroupAvatarStack summary={summary} preset="header" />;
  return (
    <div data-testid="group-info-identity" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, paddingTop: 8 }}>
      {canEdit ? (
        <Button variant="text" onClick={onPhoto} aria-label={t(photoUrl ? 'chat.group.info.changePhoto' : 'chat.group.info.addPhoto')} data-testid="group-info-photo">{photo}</Button>
      ) : photo}
      <span style={{ display: 'flex', alignItems: 'center', gap: 4, maxWidth: '100%' }}>
        <Typography variant="h3" color="strong" component="h2" style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</Typography>
        {canEdit && (
          <IconButton size="small" variant="text" aria-label={t('chat.group.info.editName')} onClick={onName} data-testid="group-info-edit-name"><Pencil size={16} aria-hidden /></IconButton>
        )}
      </span>
      {showsMemberCount(summary) && <Typography variant="p3" color="secondary" component="span">{memberCountLine(summary.memberCount, t)}</Typography>}
      {description ? (
        <span style={{ display: 'flex', alignItems: 'flex-start', gap: 4, width: '100%' }}>
          <Typography variant="p2" color="primary" component="p" dir="auto" style={{ flex: 1, margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }} data-testid="group-info-description">{description}</Typography>
          {canEdit && (
            <IconButton size="small" variant="text" aria-label={t('chat.group.info.editDescription')} onClick={onDescription}><Pencil size={16} aria-hidden /></IconButton>
          )}
        </span>
      ) : canEdit ? (
        <Button variant="text" size="small" onClick={onDescription} data-testid="group-info-add-description">{t('chat.group.info.addDescription')}</Button>
      ) : null}
    </div>
  );
}

/** One drill-in row of the group info (settings, link, requests), with an optional count. */
function InfoRow({ icon, label, value, badge, testId, onClick }: { icon: ReactNode; label: string; value?: string; badge?: number | null; testId: string; onClick: () => void }) {
  return (
    <li>
      <Button variant="text" fullWidth onClick={onClick} data-testid={testId} startIcon={icon}
        endIcon={<ChevronRight className="dir-flip" size={16} aria-hidden />}
        style={{ justifyContent: 'flex-start', textAlign: 'start' }}>
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
          {label}
          {badge ? <span data-testid={`${testId}-badge`} style={{ fontSize: 12, lineHeight: '16px', color: 'var(--ds-text-secondary)' }}>{badge}</span> : null}
          {value ? <span data-testid={`${testId}-value`} style={{ marginInlineStart: 'auto', fontSize: 12, lineHeight: '16px', color: 'var(--ds-text-secondary)' }}>{value}</span> : null}
        </span>
      </Button>
    </li>
  );
}

/** The photo chooser (title "Group photo"): upload one (images only, the settings avatar's upload path) or remove it. */
function GroupPhotoDialog({ hasPhoto, busy, error, onSave, onUploading, onFailed, onClose }: {
  hasPhoto: boolean;
  busy: boolean;
  error: string | null;
  onSave: (photoUrl: string) => void;
  onUploading: (on: boolean) => void;
  onFailed: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const fileRef = useRef<HTMLInputElement>(null);
  const picked = async (file: File | undefined) => {
    if (!file) return;
    onUploading(true);
    try {
      const result = await uploadAttachment(file, { imageOnly: true });
      if (!result.ok) {
        onFailed();
        return;
      }
      onSave(result.attachment.url);
    } catch {
      onFailed();
    } finally {
      onUploading(false);
    }
  };
  return (
    <Modal open onClose={onClose} width={440} maxHeightPx={650} scrollBody title={t('chat.group.info.photoSheetTitle')}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Button loading={busy} onClick={() => { logPageClickBi({ page_name: 'chat', button_name: 'group_photo_upload' }); fileRef.current?.click(); }} data-testid="group-photo-upload">
          {t('chat.group.info.uploadPhoto')}
        </Button>
        {hasPhoto && (
          <Button variant="outlined" color="destructive" disabled={busy} onClick={() => { logPageClickBi({ page_name: 'chat', button_name: 'group_photo_remove' }); onSave(''); }} data-testid="group-photo-remove">
            {t('chat.group.info.removePhoto')}
          </Button>
        )}
        {error && <Typography variant="p2" component="p" role="alert" style={{ color: 'var(--ds-text-destructive)' }}>{error}</Typography>}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          void picked(file);
        }}
      />
    </Modal>
  );
}
